import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const en = {
  nav: { services: 'Services', work: 'Our Work', about: 'About', pricing: 'Pricing', contact: 'Contact', audit: 'Free Audit' },
  hero: {
    eyebrow: 'Horizon Digital Agency',
    prefix: 'We help businesses',
    suffix: 'their websites.',
    sub: 'Modern websites focused on performance, design, and conversion.',
    cta1: 'Request a Website Audit',
    cta2: 'Contact Us',
  },
  services: {
    label: 'What We Do',
    title1: 'Services built around',
    title2: 'your growth',
    s1: 'Website Design & Development', d1: 'Custom business websites and landing pages built to convert visitors into clients.',
    s2: 'Website Redesign', d2: 'Improve outdated websites for better performance, user experience, and conversion.',
    s3: 'SEO Optimization', d3: 'Improve visibility on Google, increase organic traffic, and generate more leads.',
    s4: 'Branding & Design', d4: 'Logo creation and visual identity that sets your business apart from the competition.',
  },
  work: {
    label: 'Our Work',
    title1: 'Selected',
    title2: 'case studies',
    sub: 'Real results for real businesses. Every project is designed to drive measurable growth.',
  },
  process: {
    label: 'How We Work',
    title1: 'Our',
    title2: 'process',
    s1: 'Discovery', d1: 'We learn about your business, goals, and audience to create the perfect strategy.',
    s2: 'Design', d2: 'We craft a custom design that reflects your brand and engages your visitors.',
    s3: 'Development', d3: 'We build your website with clean code, fast performance, and SEO best practices.',
    s4: 'Launch & Support', d4: 'We launch your site and provide ongoing support to ensure continued success.',
  },
  stats: {
    projects: 'Projects Delivered',
    satisfaction: 'Client Satisfaction',
    countries: 'Countries Served',
    support: 'Support Response',
  },
  about: {
    title1: 'A digital agency that',
    title2: 'delivers results',
    desc1: 'Horizon Digital Agency helps businesses improve their online presence through professional web design, strategic SEO, and clear branding.',
    desc2: 'Whether you need a new website, a redesign, or better search engine visibility, we provide structured solutions that work.',
    v1_title: 'Results-Driven',
    v1_desc: 'Every design decision is backed by conversion data and business goals.',
    v2_title: 'Client-Focused',
    v2_desc: 'We work closely with you to understand your audience and deliver what they need.',
    v3_title: 'Fast Delivery',
    v3_desc: 'Professional results without long timelines. Most projects delivered within days.',
  },
  testimonials: {
    label: 'Testimonials',
    title1: 'What clients',
    title2: 'say',
    t1: 'We were losing clients because our old website looked unprofessional. Horizon redesigned everything in 10 days and our online inquiries went up by 150% the first month. The investment paid for itself within weeks.',
    t2: 'I contacted several agencies before choosing Horizon. They were the only ones who actually listened to what I needed instead of pushing templates. The final result was exactly what I envisioned for my real estate business.',
    t3: 'Our restaurant was invisible online. After Horizon built our new site and set up Google My Business properly, we started getting 3-4 new reservation requests daily just from the website. Incredible difference.',
    t4: 'I was skeptical about SEO services because I had been burned before. But Horizon delivered real results — we moved from page 5 to position 3 on Google for our main keywords in just 8 weeks. Numbers do not lie.',
    t5: 'As a startup with a tight budget, I was worried about the cost. Horizon worked with me to find the right package and the website they delivered looks like it cost 10 times what I paid. Best decision I made for my business.',
  },
  partners: {
    label: 'Technologies',
    title1: 'Tools we',
    title2: 'work with',
  },
  beforeafter: {
    label: 'Case Study',
    title1: 'From outdated to',
    title2: 'converting',
    before: 'Before',
    after: 'After',
    desc: 'Website Redesign — Complete transformation',
    tags: 'Increased Conversions / Modern Design / Better UX',
    s1: 'More Leads',
    s2: 'Better Retention',
    s3: 'Conversion Rate',
  },
  blog: {
    label: 'Insights & Resources',
    title1: 'Latest',
    title2: 'insights',
    sub: 'Tips, strategies, and guides to grow your business online.',
    read: 'read',
    read_more: 'Read More',
    b1_title: '5 Signs Your Website Needs a Redesign in 2026',
    b1_excerpt: 'Is your website driving visitors away? Here are the warning signs that it is time for a professional redesign and how it can transform your business.',
    b2_title: 'How SEO Can Double Your Revenue in 6 Months',
    b2_excerpt: 'Most businesses ignore SEO until it is too late. Learn the exact strategies we use to get clients from page 5 to position 1 on Google.',
    b3_title: 'Why Your Brand Identity Matters More Than You Think',
    b3_excerpt: 'A strong brand identity builds trust, attracts the right customers, and sets you apart from competitors. Here is how to get it right.',
    b4_title: 'The Anatomy of a High-Converting Landing Page',
    b4_excerpt: 'What makes visitors click "Buy Now" instead of leaving? We break down the key elements every landing page needs to convert.',
    b5_title: 'E-Commerce Website Mistakes That Cost You Sales',
    b5_excerpt: 'Slow load times, confusing navigation, and poor mobile experience. These common mistakes could be costing you thousands in lost revenue.',
    b6_title: 'How to Choose the Right Digital Agency for Your Business',
    b6_excerpt: 'Not all agencies are the same. Learn what to look for, the right questions to ask, and red flags to avoid when hiring a web design agency.',
  },
  cta_banner: {
    title: 'Ready to transform your online presence?',
    sub: 'Get a free website audit and discover how we can help your business grow.',
    btn: 'Request a Free Audit',
  },
  audit: {
    label: 'Free Website Audit',
    title1: 'Get your free',
    title2: 'website audit',
    sub: 'Enter your website URL and email. We will analyze your site and send you an actionable report.',
    url_placeholder: 'https://yourwebsite.com',
    email_placeholder: 'your@email.com',
    btn: 'Analyze My Website',
    loading: 'Analyzing...',
    results: 'Your Website Report',
    perf: 'Performance',
    mobile_label: 'Mobile-Friendly',
    seo_label: 'SEO',
    access_label: 'Accessibility',
    recs: 'Recommendations',
    error_msg: 'Could not analyze the website. Please check the URL and try again.',
  },
  pricing: {
    label: 'Pricing',
    title1: 'Transparent',
    title2: 'pricing',
    sub: 'No hidden fees. Simple packages designed for real business needs.',
    packages: 'Website Packages',
    addons: 'Additional Services',
    from: 'Starting from',
    popular: 'Most Popular',
    cta: 'Get Started',
    guarantee: 'All packages include a satisfaction guarantee. Not happy? We will make it right.',
    p1_name: 'Starter',
    p1_desc: 'For small businesses getting started online.',
    p1_f1: 'One-page website', p1_f2: 'Mobile responsive', p1_f3: 'Contact form', p1_f4: 'Basic SEO setup', p1_f5: '1 revision round',
    p2_name: 'Standard',
    p2_desc: 'Our most popular option for growing businesses.',
    p2_f1: 'Multi-page website (up to 5)', p2_f2: 'Custom design', p2_f3: 'Mobile responsive', p2_f4: 'SEO optimization', p2_f5: 'Analytics setup', p2_f6: '2 revision rounds',
    p3_name: 'Premium',
    p3_desc: 'Complete solution for established brands.',
    p3_f1: 'Full custom website', p3_f2: 'Advanced animations', p3_f3: 'E-commerce ready', p3_f4: 'Full SEO package', p3_f5: 'Performance optimization', p3_f6: 'Priority support', p3_f7: 'Unlimited revisions',
    a1_name: 'Landing Page', a1_price: '$200 - $500', a1_desc: 'High-converting single page for campaigns.',
    a2_name: 'SEO Basic', a2_price: '$150/month', a2_desc: 'Keyword research, on-page optimization, monthly report.',
    a3_name: 'SEO Standard', a3_price: '$300/month', a3_desc: 'Full SEO strategy, backlinks, content plan, weekly reports.',
  },
  faq: {
    label: 'FAQ',
    title1: 'Frequently asked',
    title2: 'questions',
    q1: 'How long does it take to build a website?',
    a1: 'Most projects are delivered within 1-2 weeks depending on the package. Landing pages can be ready in 3-5 days.',
    q2: 'Do you offer revisions?',
    a2: 'Yes. Starter includes 1 revision round, Standard includes 2, and Premium includes unlimited revisions until you are satisfied.',
    q3: 'Can you redesign my existing website?',
    a3: 'Absolutely. We specialize in redesigning outdated websites to improve performance, user experience, and conversion rates.',
    q4: 'What do I need to provide?',
    a4: 'We will need your logo (if available), brand colors, content/text, and any images you want to use. We can also help create or source these.',
    q5: 'Do you offer ongoing SEO services?',
    a5: 'Yes. We offer monthly SEO packages starting at $150/month for basic optimization and $300/month for a comprehensive strategy.',
    q6: 'What if I am not satisfied with the result?',
    a6: 'All packages include a satisfaction guarantee. If you are not happy, we will revise the work until it meets your expectations.',
  },
  contact: {
    label: 'Contact',
    title1: 'Ready to improve',
    title2: 'your website?',
    sub: 'Tell us about your project and we will get back to you with a clear plan and quote.',
    name: 'Name', email: 'Email', message: 'Message',
    name_ph: 'Your name', email_ph: 'your@email.com', msg_ph: 'Tell us about your project...',
    send: 'Send Message', sending: 'Sending...',
    success: 'Message sent! We will be in touch within 24 hours.',
    error: 'Something went wrong. Please try again.',
    service_label: 'Service',
    service_ph: 'Select a service...',
    select_service_error: 'Please select a service.',
    response_label: 'Response Time',
    final_title: 'Not sure where to start?',
    final_sub: 'Request a free website audit and we will analyze your current site with actionable recommendations.',
    final_cta: 'Request a Website Audit',
    response: 'Response within 24 hours',
  },
  footer: {
    desc: 'We design, rebuild, and optimize websites for businesses. Modern solutions focused on performance, design, and conversion.',
    nav: 'Navigation', services_title: 'Services', get_in_touch: 'Get in Touch',
    home: 'Home',
    rights: 'All rights reserved.',
    response: 'Response within 24 hours',
  },
  common: {
    send_message: 'Send message',
    send_inquiry: 'Send inquiry',
    sending: 'Sending…',
    whatsapp: 'WhatsApp',
    close: 'Close',
    no_payment: 'No payment is taken at this step.',
    no_payment_reply: 'No payment is taken — we\'ll reply within 48 hours.',
    what_to_change: 'What would you like changed or added?',
    what_to_change_ph: 'e.g. swap the color palette, add a booking page, plug in our Shopify catalogue, write the copy in French…',
    listed_price: 'Listed price',
    final_quote: 'Final quote depends on what you\'d like added.',
    contact_about_template: 'Contact us about this template',
    tell_us: 'Tell us what you\'d like changed or added — copy direction, extra pages, palette tweaks, integrations, anything. We\'ll write back within 48 hours with a plan, a final quote and a delivery window.',
    thank_you: 'Thank you',
    inquiry_sent: 'Your inquiry for {{name}} is on its way. We\'ll write back within 48 hours with a plan, a final quote and a delivery window.',
    your_name: 'Your name',
    email: 'Email',
    company_project: 'Company / project',
    name_email_required: 'Please add your name & email.',
    could_not_send: 'Could not send. Please try WhatsApp or email us directly.',
    browse_templates: 'Browse 28 templates →',
    open_catalogue: 'Open the catalogue →',
    open_catalogue_full: 'Open the catalogue · 28 templates',
    back_to_home: 'Back to Home',
    premium_templates: 'Premium Templates',
    need_custom: 'Need a custom design not listed here?',
    request_custom: 'Request Custom Website',
    trophy_headline1: 'Buy a',
    trophy_headline1b: 'trophy',
    trophy_headline2: 'website. Live in a week.',
    trophy_sub: 'Twenty-eight bespoke websites — fully designed, hand-coded, multi-page. Pick one, send the brief, we hand it over with your copy and your deployment.',
    eleven_plates: '— Eleven plates rotating · 28 live in the catalogue',
    headline_we: 'We',
    headline_design: 'design,',
    headline_rebuild: 'rebuild,',
    headline_and: 'and',
    headline_optimize: 'optimize',
    headline_websites: 'websites',
    headline_for: 'for businesses.',
    view_live: 'View live preview',
    view_demo: 'View demo',
    contact: 'Contact',
  },
};

const fr = {
  nav: { services: 'Services', work: 'Nos Projets', about: 'A Propos', pricing: 'Tarifs', contact: 'Contact', audit: 'Audit Gratuit' },
  hero: {
    eyebrow: 'Horizon Digital Agency',
    prefix: 'Nous aidons les entreprises a',
    suffix: 'leurs sites web.',
    sub: 'Des sites modernes axes sur la performance, le design et la conversion.',
    cta1: 'Demander un Audit Gratuit',
    cta2: 'Nous Contacter',
  },
  services: {
    label: 'Nos Services',
    title1: 'Des services axes sur',
    title2: 'votre croissance',
    s1: 'Conception & Developpement Web', d1: 'Sites web et landing pages sur mesure pour convertir vos visiteurs en clients.',
    s2: 'Refonte de Site Web', d2: 'Ameliorez vos sites obsoletes pour de meilleures performances et conversions.',
    s3: 'Optimisation SEO', d3: 'Ameliorez votre visibilite sur Google et augmentez votre trafic organique.',
    s4: 'Branding & Design', d4: 'Creation de logo et identite visuelle pour differencier votre entreprise.',
  },
  work: { label: 'Nos Projets', title1: 'Etudes de cas', title2: 'selectionnees', sub: 'Des resultats reels pour des entreprises reelles.' },
  process: {
    label: 'Notre Methode', title1: 'Notre', title2: 'processus',
    s1: 'Decouverte', d1: 'Nous apprenons a connaitre votre entreprise, vos objectifs et votre audience.',
    s2: 'Design', d2: 'Nous creons un design sur mesure qui reflete votre marque.',
    s3: 'Developpement', d3: 'Nous construisons votre site avec un code propre et des performances optimales.',
    s4: 'Lancement & Support', d4: 'Nous lancons votre site et assurons un support continu.',
  },
  stats: { projects: 'Projets Livres', satisfaction: 'Satisfaction Client', countries: 'Pays Servis', support: 'Temps de Reponse' },
  about: {
    title1: 'Une agence digitale qui',
    title2: 'livre des resultats',
    desc1: 'Horizon Digital Agency aide les entreprises a ameliorer leur presence en ligne grace a un design web professionnel, un SEO strategique et un branding clair.',
    desc2: 'Que vous ayez besoin d\'un nouveau site, d\'une refonte ou d\'une meilleure visibilite, nous fournissons des solutions structurees qui fonctionnent.',
    v1_title: 'Oriente Resultats',
    v1_desc: 'Chaque decision de design est soutenue par des donnees de conversion et des objectifs business.',
    v2_title: 'Centre Client',
    v2_desc: 'Nous travaillons en etroite collaboration avec vous pour comprendre votre audience.',
    v3_title: 'Livraison Rapide',
    v3_desc: 'Des resultats professionnels sans delais longs. La plupart des projets livres en quelques jours.',
  },
  testimonials: {
    label: 'Temoignages', title1: 'Ce que disent', title2: 'nos clients',
    t1: 'On perdait des clients a cause de notre ancien site. Horizon a tout refait en 10 jours et nos demandes ont augmente de 150% le premier mois.',
    t2: 'J\'ai contacte plusieurs agences. Horizon etait la seule a vraiment ecouter mes besoins. Le resultat etait exactement ce que j\'avais en tete pour mon business immobilier.',
    t3: 'Notre restaurant etait invisible en ligne. Apres le nouveau site, on recoit 3-4 reservations par jour juste depuis le site web.',
    t4: 'J\'etais sceptique sur le SEO. Mais on est passe de la page 5 a la position 3 sur Google en 8 semaines. Les chiffres ne mentent pas.',
    t5: 'Startup avec budget serre, j\'avais peur du cout. Le site qu\'ils ont livre a l\'air de couter 10 fois plus cher. Meilleure decision.',
  },
  partners: { label: 'Technologies', title1: 'Outils que nous', title2: 'utilisons' },
  beforeafter: { label: 'Etude de Cas', title1: 'De obsolete a', title2: 'convertissant', before: 'Avant', after: 'Apres', desc: 'Refonte de Site Web — Transformation complete', tags: 'Plus de Conversions / Design Moderne / Meilleure UX', s1: 'Plus de Prospects', s2: 'Meilleure Retention', s3: 'Taux de Conversion' },
  blog: {
    label: 'Ressources',
    title1: 'Dernieres',
    title2: 'actualites',
    sub: 'Conseils, strategies et guides pour developper votre business en ligne.',
    read: 'lecture',
    read_more: 'Lire la suite',
    b1_title: '5 signes que votre site web a besoin d\'une refonte en 2026',
    b1_excerpt: 'Votre site fait fuir les visiteurs? Voici les signes d\'alerte et comment une refonte peut transformer votre business.',
    b2_title: 'Comment le SEO peut doubler votre chiffre d\'affaires en 6 mois',
    b2_excerpt: 'La plupart des entreprises ignorent le SEO. Decouvrez les strategies pour passer de la page 5 a la position 1 sur Google.',
    b3_title: 'Pourquoi votre identite de marque compte plus que vous ne le pensez',
    b3_excerpt: 'Une identite de marque forte construit la confiance et attire les bons clients.',
    b4_title: 'L\'anatomie d\'une landing page qui convertit',
    b4_excerpt: 'Qu\'est-ce qui pousse les visiteurs a cliquer? Les elements cles de chaque landing page.',
    b5_title: 'Erreurs de site e-commerce qui vous coutent des ventes',
    b5_excerpt: 'Temps de chargement lents, navigation confuse. Ces erreurs pourraient vous couter des milliers.',
    b6_title: 'Comment choisir la bonne agence digitale',
    b6_excerpt: 'Toutes les agences ne se valent pas. Ce qu\'il faut chercher et les questions a poser.',
  },
  cta_banner: { title: 'Pret a transformer votre presence en ligne?', sub: 'Obtenez un audit gratuit de votre site web.', btn: 'Demander un Audit Gratuit' },
  audit: {
    label: 'Audit Gratuit', title1: 'Obtenez votre', title2: 'audit gratuit',
    sub: 'Entrez votre URL et email. Nous analyserons votre site.',
    url_placeholder: 'https://votresite.com', email_placeholder: 'votre@email.com',
    btn: 'Analyser Mon Site', loading: 'Analyse en cours...', results: 'Votre Rapport',
    perf: 'Performance', mobile_label: 'Mobile', seo_label: 'SEO', access_label: 'Accessibilite',
    recs: 'Recommandations', error_msg: 'Impossible d\'analyser le site. Verifiez l\'URL et reessayez.',
  },
  pricing: {
    label: 'Tarifs', title1: 'Tarification', title2: 'transparente',
    sub: 'Pas de frais caches. Des forfaits simples pour des besoins reels.',
    packages: 'Forfaits Site Web', addons: 'Services Supplementaires', from: 'A partir de',
    popular: 'Le Plus Populaire', cta: 'Commencer',
    guarantee: 'Tous les forfaits incluent une garantie de satisfaction.',
    p1_name: 'Starter', p1_desc: 'Pour les petites entreprises qui demarrent en ligne.',
    p1_f1: 'Site une page', p1_f2: 'Responsive mobile', p1_f3: 'Formulaire de contact', p1_f4: 'SEO de base', p1_f5: '1 tour de revision',
    p2_name: 'Standard', p2_desc: 'Notre option la plus populaire pour les entreprises en croissance.',
    p2_f1: 'Site multi-pages (jusqu\'a 5)', p2_f2: 'Design personnalise', p2_f3: 'Responsive mobile', p2_f4: 'Optimisation SEO', p2_f5: 'Configuration analytics', p2_f6: '2 tours de revision',
    p3_name: 'Premium', p3_desc: 'Solution complete pour les marques etablies.',
    p3_f1: 'Site entierement personnalise', p3_f2: 'Animations avancees', p3_f3: 'E-commerce pret', p3_f4: 'Pack SEO complet', p3_f5: 'Optimisation des performances', p3_f6: 'Support prioritaire', p3_f7: 'Revisions illimitees',
    a1_name: 'Landing Page', a1_price: '200$ - 500$', a1_desc: 'Page unique haute conversion pour campagnes.',
    a2_name: 'SEO Basique', a2_price: '150$/mois', a2_desc: 'Recherche de mots-cles, optimisation on-page, rapport mensuel.',
    a3_name: 'SEO Standard', a3_price: '300$/mois', a3_desc: 'Strategie SEO complete, backlinks, plan de contenu.',
  },
  faq: {
    label: 'FAQ', title1: 'Questions', title2: 'frequentes',
    q1: 'Combien de temps faut-il pour creer un site web?',
    a1: 'La plupart des projets sont livres en 1-2 semaines. Les landing pages peuvent etre pretes en 3-5 jours.',
    q2: 'Proposez-vous des revisions?',
    a2: 'Oui. Starter inclut 1 tour, Standard 2, et Premium des revisions illimitees.',
    q3: 'Pouvez-vous redesigner mon site existant?',
    a3: 'Absolument. Nous sommes specialises dans la refonte de sites obsoletes.',
    q4: 'Que dois-je fournir?',
    a4: 'Votre logo, vos couleurs de marque, le contenu et les images. Nous pouvons aussi les creer.',
    q5: 'Proposez-vous des services SEO continus?',
    a5: 'Oui. Forfaits mensuels a partir de 150$/mois pour le basique et 300$/mois pour une strategie complete.',
    q6: 'Et si je ne suis pas satisfait?',
    a6: 'Tous les forfaits incluent une garantie de satisfaction. Nous reviserons jusqu\'a ce que vous soyez satisfait.',
  },
  contact: {
    label: 'Contact', title1: 'Pret a ameliorer', title2: 'votre site web?',
    sub: 'Parlez-nous de votre projet.',
    name: 'Nom', email: 'Email', message: 'Message',
    name_ph: 'Votre nom', email_ph: 'votre@email.com', msg_ph: 'Parlez-nous de votre projet...',
    send: 'Envoyer', sending: 'Envoi en cours...',
    success: 'Message envoye! Nous vous recontacterons sous 24h.', error: 'Une erreur est survenue.',
    service_label: 'Service',
    service_ph: 'Choisir un service...',
    select_service_error: 'Veuillez choisir un service.',
    response_label: 'Temps de reponse',
    final_title: 'Vous ne savez pas par ou commencer?', final_sub: 'Demandez un audit gratuit.',
    final_cta: 'Demander un Audit', response: 'Reponse sous 24 heures',
  },
  footer: {
    desc: 'Nous concevons et optimisons les sites web des entreprises.',
    nav: 'Navigation', services_title: 'Services', get_in_touch: 'Contact',
    home: 'Accueil', rights: 'Tous droits reserves.', response: 'Reponse sous 24 heures',
  },
  common: {
    send_message: 'Envoyer le message',
    send_inquiry: 'Envoyer la demande',
    sending: 'Envoi en cours…',
    whatsapp: 'WhatsApp',
    close: 'Fermer',
    no_payment: 'Aucun paiement n\'est pris a cette etape.',
    no_payment_reply: 'Aucun paiement n\'est pris — nous repondrons sous 48 heures.',
    what_to_change: 'Que souhaitez-vous modifier ou ajouter?',
    what_to_change_ph: 'ex. changer la palette de couleurs, ajouter une page de reservation, brancher notre catalogue Shopify, rediger le contenu en francais…',
    listed_price: 'Prix affiche',
    final_quote: 'Le devis final depend de ce que vous souhaitez ajouter.',
    contact_about_template: 'Nous contacter a propos de ce template',
    tell_us: 'Dites-nous ce que vous souhaitez modifier ou ajouter — direction du texte, pages supplementaires, ajustements de palette, integrations, tout ce que vous voulez. Nous reviendrons vers vous sous 48 heures avec un plan, un devis final et un delai de livraison.',
    thank_you: 'Merci',
    inquiry_sent: 'Votre demande pour {{name}} est en route. Nous reviendrons vers vous sous 48 heures avec un plan, un devis final et un delai de livraison.',
    your_name: 'Votre nom',
    email: 'Email',
    company_project: 'Entreprise / projet',
    name_email_required: 'Veuillez ajouter votre nom et email.',
    could_not_send: 'Envoi impossible. Essayez WhatsApp ou envoyez-nous un email directement.',
    browse_templates: 'Parcourir les 28 templates →',
    open_catalogue: 'Ouvrir le catalogue →',
    open_catalogue_full: 'Ouvrir le catalogue · 28 templates',
    back_to_home: 'Retour a l\'accueil',
    premium_templates: 'Templates Premium',
    need_custom: 'Besoin d\'un design sur mesure non liste ici?',
    request_custom: 'Demander un Site Sur Mesure',
    trophy_headline1: 'Achetez un',
    trophy_headline1b: 'site-trophee',
    trophy_headline2: 'site web. En ligne en une semaine.',
    trophy_sub: 'Vingt-huit sites web sur mesure — entierement concus, codes a la main, multi-pages. Choisissez-en un, envoyez le brief, nous vous le livrons avec votre contenu et votre deploiement.',
    eleven_plates: '— Onze plaques en rotation · 28 en ligne dans le catalogue',
    headline_we: 'Nous',
    headline_design: 'concevons,',
    headline_rebuild: 'reconstruisons,',
    headline_and: 'et',
    headline_optimize: 'optimisons',
    headline_websites: 'les sites web',
    headline_for: 'pour les entreprises.',
    view_live: 'Voir l\'apercu',
    view_demo: 'Voir la demo',
    contact: 'Contact',
  },
};

const es = {
  nav: { services: 'Servicios', work: 'Proyectos', about: 'Nosotros', pricing: 'Precios', contact: 'Contacto', audit: 'Auditoria Gratis' },
  hero: {
    eyebrow: 'Horizon Digital Agency',
    prefix: 'Ayudamos a las empresas a',
    suffix: 'sus sitios web.',
    sub: 'Sitios web modernos enfocados en rendimiento, diseno y conversion.',
    cta1: 'Solicitar Auditoria Gratuita',
    cta2: 'Contactanos',
  },
  services: {
    label: 'Servicios', title1: 'Servicios enfocados en', title2: 'tu crecimiento',
    s1: 'Diseno y Desarrollo Web', d1: 'Sitios web personalizados para convertir visitantes en clientes.',
    s2: 'Rediseno de Sitios Web', d2: 'Mejora sitios obsoletos para mejor rendimiento.',
    s3: 'Optimizacion SEO', d3: 'Mejora tu visibilidad en Google.',
    s4: 'Branding y Diseno', d4: 'Creacion de logo e identidad visual.',
  },
  work: { label: 'Proyectos', title1: 'Estudios de caso', title2: 'seleccionados', sub: 'Resultados reales para empresas reales.' },
  process: {
    label: 'Proceso', title1: 'Nuestro', title2: 'proceso',
    s1: 'Descubrimiento', d1: 'Conocemos tu negocio y objetivos.',
    s2: 'Diseno', d2: 'Creamos un diseno personalizado.',
    s3: 'Desarrollo', d3: 'Construimos tu sitio con codigo limpio.',
    s4: 'Lanzamiento y Soporte', d4: 'Lanzamos tu sitio y brindamos soporte continuo.',
  },
  stats: { projects: 'Proyectos Entregados', satisfaction: 'Satisfaccion del Cliente', countries: 'Paises Atendidos', support: 'Tiempo de Respuesta' },
  about: {
    title1: 'Una agencia digital que',
    title2: 'entrega resultados',
    desc1: 'Horizon Digital Agency ayuda a las empresas a mejorar su presencia en linea con diseno web profesional, SEO estrategico y branding claro.',
    desc2: 'Ya sea que necesites un nuevo sitio, un rediseno o mejor visibilidad, ofrecemos soluciones estructuradas que funcionan.',
    v1_title: 'Orientado a Resultados',
    v1_desc: 'Cada decision de diseno esta respaldada por datos de conversion y objetivos de negocio.',
    v2_title: 'Enfocado en el Cliente',
    v2_desc: 'Trabajamos contigo para entender a tu audiencia y entregar lo que necesitan.',
    v3_title: 'Entrega Rapida',
    v3_desc: 'Resultados profesionales sin largos plazos. La mayoria de proyectos entregados en dias.',
  },
  testimonials: {
    label: 'Testimonios', title1: 'Lo que dicen', title2: 'nuestros clientes',
    t1: 'Perdiamos clientes porque nuestro sitio viejo no se veia profesional. Horizon lo rediseno en 10 dias y las consultas aumentaron un 150%.',
    t2: 'Contacte varias agencias. Horizon fue la unica que escucho lo que necesitaba en vez de ofrecer plantillas. El resultado fue exactamente lo que imaginaba.',
    t3: 'Nuestro restaurante era invisible online. Despues del nuevo sitio, recibimos 3-4 reservas diarias solo desde la web.',
    t4: 'Era esceptico con el SEO. Pero pasamos de la pagina 5 a la posicion 3 en Google en 8 semanas. Los numeros no mienten.',
    t5: 'Como startup con presupuesto ajustado, temia el costo. El sitio que entregaron parece costar 10 veces mas. La mejor decision para mi negocio.',
  },
  partners: { label: 'Tecnologias', title1: 'Herramientas que', title2: 'usamos' },
  beforeafter: { label: 'Caso de Estudio', title1: 'De obsoleto a', title2: 'convertir', before: 'Antes', after: 'Despues', desc: 'Rediseno de Sitio Web — Transformacion completa', tags: 'Mas Conversiones / Diseno Moderno / Mejor UX', s1: 'Mas Prospectos', s2: 'Mejor Retencion', s3: 'Tasa de Conversion' },
  blog: {
    label: 'Recursos',
    title1: 'Ultimas',
    title2: 'novedades',
    sub: 'Consejos, estrategias y guias para hacer crecer tu negocio en linea.',
    read: 'lectura',
    read_more: 'Leer mas',
    b1_title: '5 senales de que tu sitio web necesita un rediseno en 2026',
    b1_excerpt: 'Tu sitio esta ahuyentando visitantes? Aqui las senales de alerta y como un rediseno puede transformar tu negocio.',
    b2_title: 'Como el SEO puede duplicar tus ingresos en 6 meses',
    b2_excerpt: 'La mayoria de empresas ignoran el SEO. Descubre las estrategias para llegar a la posicion 1 en Google.',
    b3_title: 'Por que tu identidad de marca importa mas de lo que crees',
    b3_excerpt: 'Una identidad de marca fuerte construye confianza y atrae a los clientes correctos.',
    b4_title: 'La anatomia de una landing page que convierte',
    b4_excerpt: 'Que hace que los visitantes hagan clic? Los elementos clave de toda landing page.',
    b5_title: 'Errores de e-commerce que te cuestan ventas',
    b5_excerpt: 'Tiempos de carga lentos, navegacion confusa. Estos errores podrian costarte miles.',
    b6_title: 'Como elegir la agencia digital correcta',
    b6_excerpt: 'No todas las agencias son iguales. Que buscar y que preguntas hacer.',
  },
  cta_banner: { title: 'Listo para transformar tu presencia en linea?', sub: 'Obten una auditoria gratuita de tu sitio web.', btn: 'Solicitar Auditoria Gratuita' },
  audit: {
    label: 'Auditoria Gratis', title1: 'Obten tu', title2: 'auditoria gratis',
    sub: 'Ingresa tu URL y correo.',
    url_placeholder: 'https://tusitio.com', email_placeholder: 'tu@email.com',
    btn: 'Analizar Mi Sitio', loading: 'Analizando...', results: 'Tu Reporte',
    perf: 'Rendimiento', mobile_label: 'Movil', seo_label: 'SEO', access_label: 'Accesibilidad',
    recs: 'Recomendaciones', error_msg: 'No se pudo analizar el sitio. Verifica la URL e intenta de nuevo.',
  },
  pricing: {
    label: 'Precios', title1: 'Precios', title2: 'transparentes',
    sub: 'Sin costos ocultos.',
    packages: 'Paquetes Web', addons: 'Servicios Adicionales', from: 'Desde',
    popular: 'Mas Popular', cta: 'Comenzar',
    guarantee: 'Todos los paquetes incluyen garantia de satisfaccion.',
    p1_name: 'Starter', p1_desc: 'Para pequenas empresas que inician en linea.',
    p1_f1: 'Sitio de una pagina', p1_f2: 'Responsive movil', p1_f3: 'Formulario de contacto', p1_f4: 'SEO basico', p1_f5: '1 ronda de revision',
    p2_name: 'Standard', p2_desc: 'Nuestra opcion mas popular para empresas en crecimiento.',
    p2_f1: 'Sitio multi-pagina (hasta 5)', p2_f2: 'Diseno personalizado', p2_f3: 'Responsive movil', p2_f4: 'Optimizacion SEO', p2_f5: 'Configuracion de analytics', p2_f6: '2 rondas de revision',
    p3_name: 'Premium', p3_desc: 'Solucion completa para marcas establecidas.',
    p3_f1: 'Sitio completamente personalizado', p3_f2: 'Animaciones avanzadas', p3_f3: 'E-commerce listo', p3_f4: 'Paquete SEO completo', p3_f5: 'Optimizacion de rendimiento', p3_f6: 'Soporte prioritario', p3_f7: 'Revisiones ilimitadas',
    a1_name: 'Landing Page', a1_price: '$200 - $500', a1_desc: 'Pagina unica de alta conversion para campanas.',
    a2_name: 'SEO Basico', a2_price: '$150/mes', a2_desc: 'Investigacion de palabras clave, optimizacion on-page, reporte mensual.',
    a3_name: 'SEO Standard', a3_price: '$300/mes', a3_desc: 'Estrategia SEO completa, backlinks, plan de contenido.',
  },
  faq: {
    label: 'FAQ', title1: 'Preguntas', title2: 'frecuentes',
    q1: 'Cuanto tiempo toma construir un sitio web?',
    a1: 'La mayoria se entregan en 1-2 semanas. Las landing pages en 3-5 dias.',
    q2: 'Ofrecen revisiones?',
    a2: 'Si. Starter incluye 1 ronda, Standard 2, y Premium revisiones ilimitadas.',
    q3: 'Pueden redisenar mi sitio existente?',
    a3: 'Absolutamente. Nos especializamos en redisenar sitios obsoletos.',
    q4: 'Que necesito proporcionar?',
    a4: 'Tu logo, colores de marca, contenido e imagenes. Tambien podemos crearlos.',
    q5: 'Ofrecen servicios SEO continuos?',
    a5: 'Si. Paquetes mensuales desde $150/mes para basico y $300/mes para estrategia completa.',
    q6: 'Que pasa si no estoy satisfecho?',
    a6: 'Todos los paquetes incluyen garantia. Revisaremos hasta que estes satisfecho.',
  },
  contact: {
    label: 'Contacto', title1: 'Listo para mejorar', title2: 'tu sitio web?',
    sub: 'Cuentanos sobre tu proyecto.',
    name: 'Nombre', email: 'Email', message: 'Mensaje',
    name_ph: 'Tu nombre', email_ph: 'tu@email.com', msg_ph: 'Cuentanos sobre tu proyecto...',
    send: 'Enviar', sending: 'Enviando...',
    success: 'Mensaje enviado! Te contactaremos en 24 horas.', error: 'Algo salio mal.',
    service_label: 'Servicio',
    service_ph: 'Selecciona un servicio...',
    select_service_error: 'Por favor selecciona un servicio.',
    response_label: 'Tiempo de respuesta',
    final_title: 'No sabes por donde empezar?', final_sub: 'Solicita una auditoria gratuita.',
    final_cta: 'Solicitar Auditoria', response: 'Respuesta en 24 horas',
  },
  footer: {
    desc: 'Disenamos y optimizamos sitios web para empresas.',
    nav: 'Navegacion', services_title: 'Servicios', get_in_touch: 'Contacto',
    home: 'Inicio', rights: 'Todos los derechos reservados.', response: 'Respuesta en 24 horas',
  },
  common: {
    send_message: 'Enviar mensaje',
    send_inquiry: 'Enviar consulta',
    sending: 'Enviando…',
    whatsapp: 'WhatsApp',
    close: 'Cerrar',
    no_payment: 'No se realiza ningun pago en este paso.',
    no_payment_reply: 'No se realiza ningun pago — responderemos en 48 horas.',
    what_to_change: 'Que te gustaria cambiar o anadir?',
    what_to_change_ph: 'ej. cambiar la paleta de colores, anadir una pagina de reservas, conectar nuestro catalogo de Shopify, escribir el contenido en frances…',
    listed_price: 'Precio indicado',
    final_quote: 'El presupuesto final depende de lo que quieras anadir.',
    contact_about_template: 'Contactanos sobre esta plantilla',
    tell_us: 'Cuentanos que te gustaria cambiar o anadir — direccion del contenido, paginas adicionales, ajustes de paleta, integraciones, cualquier cosa. Te responderemos en 48 horas con un plan, un presupuesto final y un plazo de entrega.',
    thank_you: 'Gracias',
    inquiry_sent: 'Tu consulta para {{name}} esta en camino. Te responderemos en 48 horas con un plan, un presupuesto final y un plazo de entrega.',
    your_name: 'Tu nombre',
    email: 'Email',
    company_project: 'Empresa / proyecto',
    name_email_required: 'Por favor anade tu nombre y email.',
    could_not_send: 'No se pudo enviar. Prueba WhatsApp o envianos un email directamente.',
    browse_templates: 'Ver las 28 plantillas →',
    open_catalogue: 'Abrir el catalogo →',
    open_catalogue_full: 'Abrir el catalogo · 28 plantillas',
    back_to_home: 'Volver al Inicio',
    premium_templates: 'Plantillas Premium',
    need_custom: 'Necesitas un diseno personalizado no listado aqui?',
    request_custom: 'Solicitar Sitio Personalizado',
    trophy_headline1: 'Compra un',
    trophy_headline1b: 'sitio-trofeo',
    trophy_headline2: 'sitio web. En linea en una semana.',
    trophy_sub: 'Veintiocho sitios web a medida — totalmente disenados, codificados a mano, multi-pagina. Elige uno, envia el brief, te lo entregamos con tu contenido y tu despliegue.',
    eleven_plates: '— Once placas rotando · 28 en el catalogo',
    headline_we: 'Nosotros',
    headline_design: 'disenamos,',
    headline_rebuild: 'reconstruimos,',
    headline_and: 'y',
    headline_optimize: 'optimizamos',
    headline_websites: 'sitios web',
    headline_for: 'para empresas.',
    view_live: 'Ver vista previa',
    view_demo: 'Ver demo',
    contact: 'Contacto',
  },
};

const ar = {
  nav: { services: 'الخدمات', work: 'أعمالنا', about: 'من نحن', pricing: 'الأسعار', contact: 'اتصل بنا', audit: 'تدقيق مجاني' },
  hero: {
    eyebrow: 'هورايزن ديجيتال',
    prefix: 'نساعد الشركات على',
    suffix: 'مواقعهم الإلكترونية.',
    sub: 'مواقع حديثة تركز على الأداء والتصميم والتحويل.',
    cta1: 'اطلب تدقيق مجاني',
    cta2: 'اتصل بنا',
  },
  services: {
    label: 'خدماتنا', title1: 'خدمات مبنية على', title2: 'نموك',
    s1: 'تصميم وتطوير المواقع', d1: 'مواقع مخصصة لتحويل الزوار إلى عملاء.',
    s2: 'إعادة تصميم المواقع', d2: 'تحسين المواقع القديمة للأداء الأفضل.',
    s3: 'تحسين محركات البحث', d3: 'تحسين الظهور على جوجل وزيادة الزيارات.',
    s4: 'العلامة التجارية والتصميم', d4: 'إنشاء شعار وهوية بصرية.',
  },
  work: { label: 'أعمالنا', title1: 'دراسات حالة', title2: 'مختارة', sub: 'نتائج حقيقية لشركات حقيقية.' },
  process: {
    label: 'طريقة عملنا', title1: 'عملية', title2: 'العمل',
    s1: 'الاكتشاف', d1: 'نتعرف على عملك وأهدافك.',
    s2: 'التصميم', d2: 'نصمم تصميماً يعكس علامتك التجارية.',
    s3: 'التطوير', d3: 'نبني موقعك بكود نظيف وأداء سريع.',
    s4: 'الإطلاق والدعم', d4: 'نطلق موقعك ونوفر دعماً مستمراً.',
  },
  stats: { projects: 'مشروع منجز', satisfaction: 'رضا العملاء', countries: 'دولة مخدومة', support: 'وقت الاستجابة' },
  about: {
    title1: 'وكالة رقمية',
    title2: 'تحقق النتائج',
    desc1: 'هورايزن ديجيتال تساعد الشركات على تحسين تواجدها الرقمي من خلال تصميم ويب احترافي وسيو استراتيجي وبراندنج واضح.',
    desc2: 'سواء كنت تحتاج موقعاً جديداً أو إعادة تصميم أو رؤية أفضل في محركات البحث، نقدم حلولاً منظمة تعمل.',
    v1_title: 'موجه نحو النتائج',
    v1_desc: 'كل قرار تصميم مدعوم ببيانات التحويل وأهداف العمل.',
    v2_title: 'يركز على العميل',
    v2_desc: 'نعمل معك عن كثب لفهم جمهورك وتقديم ما يحتاجونه.',
    v3_title: 'تسليم سريع',
    v3_desc: 'نتائج احترافية بدون مواعيد طويلة. معظم المشاريع تُسلّم في أيام.',
  },
  testimonials: {
    label: 'آراء العملاء', title1: 'ماذا يقول', title2: 'عملاؤنا',
    t1: 'كنا نخسر عملاء بسبب موقعنا القديم. هورايزن أعاد تصميم كل شيء في 10 أيام وزادت استفساراتنا 150% في الشهر الأول.',
    t2: 'تواصلت مع عدة وكالات. هورايزن كانوا الوحيدين الذين استمعوا لاحتياجاتي فعلاً. النتيجة كانت بالضبط ما تصورته.',
    t3: 'مطعمنا كان غير مرئي على الإنترنت. بعد الموقع الجديد بدأنا نستقبل 3-4 حجوزات يومياً من الموقع فقط.',
    t4: 'كنت متشككاً بخصوص السيو. لكن انتقلنا من الصفحة 5 للمركز 3 في جوجل خلال 8 أسابيع. الأرقام لا تكذب.',
    t5: 'كشركة ناشئة بميزانية محدودة كنت قلقاً من التكلفة. الموقع الذي سلموه يبدو وكأنه كلف 10 أضعاف ما دفعت.',
  },
  partners: { label: 'التقنيات', title1: 'الأدوات التي', title2: 'نستخدمها' },
  beforeafter: { label: 'دراسة حالة', title1: 'من قديم إلى', title2: 'محوّل', before: 'قبل', after: 'بعد', desc: 'إعادة تصميم الموقع — تحول كامل', tags: 'زيادة التحويلات / تصميم عصري / تجربة أفضل', s1: 'عملاء محتملين أكثر', s2: 'احتفاظ أفضل', s3: 'معدل التحويل' },
  blog: {
    label: 'مقالات ومصادر',
    title1: 'آخر',
    title2: 'المقالات',
    sub: 'نصائح واستراتيجيات لتنمية أعمالك عبر الإنترنت.',
    read: 'قراءة',
    read_more: 'اقرأ المزيد',
    b1_title: '5 علامات أن موقعك يحتاج إعادة تصميم في 2026',
    b1_excerpt: 'هل موقعك يطرد الزوار؟ إليك علامات التحذير وكيف يمكن لإعادة التصميم تحويل أعمالك.',
    b2_title: 'كيف يمكن للسيو مضاعفة إيراداتك في 6 أشهر',
    b2_excerpt: 'معظم الشركات تتجاهل السيو. تعرف على الاستراتيجيات للوصول للمركز الأول في جوجل.',
    b3_title: 'لماذا هوية علامتك التجارية أهم مما تعتقد',
    b3_excerpt: 'هوية العلامة التجارية القوية تبني الثقة وتجذب العملاء المناسبين.',
    b4_title: 'تشريح صفحة هبوط تحقق تحويلات عالية',
    b4_excerpt: 'ما الذي يجعل الزوار ينقرون؟ العناصر الأساسية لكل صفحة هبوط.',
    b5_title: 'أخطاء مواقع التجارة الإلكترونية التي تكلفك مبيعات',
    b5_excerpt: 'أوقات تحميل بطيئة وتنقل مربك. هذه الأخطاء قد تكلفك آلاف الدولارات.',
    b6_title: 'كيف تختار الوكالة الرقمية المناسبة لأعمالك',
    b6_excerpt: 'ليست كل الوكالات متشابهة. ما يجب البحث عنه والأسئلة التي يجب طرحها.',
  },
  cta_banner: { title: 'هل أنت مستعد لتحسين حضورك الرقمي؟', sub: 'احصل على تدقيق مجاني لموقعك.', btn: 'اطلب تدقيق مجاني' },
  audit: {
    label: 'تدقيق مجاني', title1: 'احصل على', title2: 'تدقيق مجاني',
    sub: 'أدخل رابط موقعك وبريدك الإلكتروني.',
    url_placeholder: 'https://موقعك.com', email_placeholder: 'بريدك@email.com',
    btn: 'تحليل موقعي', loading: 'جاري التحليل...', results: 'تقرير موقعك',
    perf: 'الأداء', mobile_label: 'التوافق مع الجوال', seo_label: 'سيو', access_label: 'إمكانية الوصول',
    recs: 'التوصيات', error_msg: 'تعذر تحليل الموقع. تحقق من الرابط وحاول مرة أخرى.',
  },
  pricing: {
    label: 'الأسعار', title1: 'أسعار', title2: 'شفافة',
    sub: 'بدون رسوم مخفية.',
    packages: 'باقات المواقع', addons: 'خدمات إضافية', from: 'ابتداءً من',
    popular: 'الأكثر شعبية', cta: 'ابدأ الآن',
    guarantee: 'جميع الباقات تشمل ضمان الرضا.',
    p1_name: 'ستارتر', p1_desc: 'للشركات الصغيرة التي تبدأ عبر الإنترنت.',
    p1_f1: 'موقع صفحة واحدة', p1_f2: 'متجاوب مع الجوال', p1_f3: 'نموذج اتصال', p1_f4: 'إعداد سيو أساسي', p1_f5: 'جولة مراجعة واحدة',
    p2_name: 'ستاندرد', p2_desc: 'الخيار الأكثر شعبية للشركات النامية.',
    p2_f1: 'موقع متعدد الصفحات (حتى 5)', p2_f2: 'تصميم مخصص', p2_f3: 'متجاوب مع الجوال', p2_f4: 'تحسين سيو', p2_f5: 'إعداد التحليلات', p2_f6: 'جولتا مراجعة',
    p3_name: 'بريميوم', p3_desc: 'حل كامل للعلامات التجارية الراسخة.',
    p3_f1: 'موقع مخصص بالكامل', p3_f2: 'رسوم متحركة متقدمة', p3_f3: 'جاهز للتجارة الإلكترونية', p3_f4: 'حزمة سيو كاملة', p3_f5: 'تحسين الأداء', p3_f6: 'دعم أولوية', p3_f7: 'مراجعات غير محدودة',
    a1_name: 'صفحة هبوط', a1_price: '$200 - $500', a1_desc: 'صفحة واحدة عالية التحويل للحملات.',
    a2_name: 'سيو أساسي', a2_price: '$150/شهر', a2_desc: 'بحث كلمات مفتاحية، تحسين الصفحة، تقرير شهري.',
    a3_name: 'سيو ستاندرد', a3_price: '$300/شهر', a3_desc: 'استراتيجية سيو كاملة، روابط خلفية، خطة محتوى.',
  },
  faq: {
    label: 'أسئلة شائعة', title1: 'أسئلة', title2: 'شائعة',
    q1: 'كم يستغرق بناء موقع ويب؟',
    a1: 'معظم المشاريع تُسلّم خلال 1-2 أسبوع. صفحات الهبوط في 3-5 أيام.',
    q2: 'هل تقدمون مراجعات؟',
    a2: 'نعم. ستارتر يشمل جولة واحدة، ستاندرد 2، وبريميوم مراجعات غير محدودة.',
    q3: 'هل يمكنكم إعادة تصميم موقعي الحالي؟',
    a3: 'بالتأكيد. نحن متخصصون في إعادة تصميم المواقع القديمة.',
    q4: 'ماذا أحتاج أن أوفر؟',
    a4: 'شعارك وألوان علامتك التجارية والمحتوى والصور. يمكننا أيضاً إنشاؤها.',
    q5: 'هل تقدمون خدمات سيو مستمرة؟',
    a5: 'نعم. باقات شهرية تبدأ من $150/شهر للأساسي و$300/شهر للاستراتيجية الكاملة.',
    q6: 'ماذا لو لم أكن راضياً؟',
    a6: 'جميع الباقات تشمل ضمان الرضا. سنراجع حتى تكون راضياً.',
  },
  contact: {
    label: 'اتصل بنا', title1: 'مستعد لتحسين', title2: 'موقعك؟',
    sub: 'أخبرنا عن مشروعك.',
    name: 'الاسم', email: 'البريد', message: 'الرسالة',
    name_ph: 'اسمك', email_ph: 'بريدك@email.com', msg_ph: 'أخبرنا عن مشروعك...',
    send: 'إرسال', sending: 'جاري الإرسال...',
    success: 'تم الإرسال! سنتواصل معك خلال 24 ساعة.', error: 'حدث خطأ.',
    service_label: 'الخدمة',
    service_ph: 'اختر خدمة...',
    select_service_error: 'يرجى اختيار خدمة.',
    response_label: 'وقت الرد',
    final_title: 'لا تعرف من أين تبدأ؟', final_sub: 'اطلب تدقيق مجاني لموقعك.',
    final_cta: 'اطلب تدقيق', response: 'الرد خلال 24 ساعة',
  },
  footer: {
    desc: 'نصمم ونحسن المواقع للشركات.',
    nav: 'التنقل', services_title: 'الخدمات', get_in_touch: 'تواصل معنا',
    home: 'الرئيسية', rights: 'جميع الحقوق محفوظة.', response: 'الرد خلال 24 ساعة',
  },
  common: {
    send_message: 'إرسال الرسالة',
    send_inquiry: 'إرسال الطلب',
    sending: 'جاري الإرسال…',
    whatsapp: 'واتساب',
    close: 'إغلاق',
    no_payment: 'لا يتم استيفاء أي دفع في هذه المرحلة.',
    no_payment_reply: 'لا يتم استيفاء أي دفع — سنرد خلال 48 ساعة.',
    what_to_change: 'ما الذي تود تغييره أو إضافته؟',
    what_to_change_ph: 'مثل: تغيير الألوان، إضافة صفحة حجز، ربط متجر Shopify، ترجمة النص…',
    listed_price: 'السعر المعلن',
    final_quote: 'السعر النهائي يعتمد على ما تريد إضافته.',
    contact_about_template: 'تواصل معنا حول هذا القالب',
    tell_us: 'أخبرنا بما تريد تغييره أو إضافته — توجيه المحتوى، صفحات إضافية، ألوان، تكاملات، أي شيء. سنرد خلال 48 ساعة بخطة وسعر نهائي ومدة تسليم.',
    thank_you: 'شكرا',
    inquiry_sent: 'طلبك لـ {{name}} في الطريق. سنرد خلال 48 ساعة بخطة وسعر نهائي ومدة تسليم.',
    your_name: 'اسمك',
    email: 'البريد الإلكتروني',
    company_project: 'الشركة / المشروع',
    name_email_required: 'يرجى إضافة اسمك وبريدك الإلكتروني.',
    could_not_send: 'تعذر الإرسال. جرب واتساب أو راسلنا مباشرة.',
    browse_templates: 'تصفح 28 قالب ←',
    open_catalogue: 'افتح الكتالوج ←',
    open_catalogue_full: 'افتح الكتالوج · 28 قالب',
    back_to_home: 'العودة للرئيسية',
    premium_templates: 'قوالب بريميوم',
    need_custom: 'تحتاج تصميم مخصص غير مذكور هنا؟',
    request_custom: 'اطلب موقعا مخصصا',
    trophy_headline1: 'اشتر',
    trophy_headline1b: 'موقعا راقيا',
    trophy_headline2: 'في غضون أسبوع.',
    trophy_sub: 'ثمانية وعشرون موقعا مصمما خصيصا — بالكامل بالتصميم والبرمجة اليدوية ومتعدد الصفحات. اختر واحدا، أرسل الموجز، نسلمك الموقع مع المحتوى والنشر.',
    eleven_plates: '— إحدى عشرة لوحة دوارة · 28 في الكتالوج',
    headline_we: 'نحن',
    headline_design: 'نصمم،',
    headline_rebuild: 'نعيد البناء،',
    headline_and: 'و',
    headline_optimize: 'نحسن',
    headline_websites: 'المواقع',
    headline_for: 'للشركات.',
    view_live: 'عرض حي',
    view_demo: 'عرض المعاينة',
    contact: 'تواصل',
  },
};

const resources = {
  en: { translation: en },
  fr: { translation: fr },
  es: { translation: es },
  ar: { translation: ar },
};

i18n.use(initReactI18next).init({
  resources,
  lng: 'en',
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

export default i18n;